import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import { IntentPreloadingStrategy } from './shared/router/intent-preloading-strategy';
import { provideHttpClient } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import { LUCIDE_ICONS } from '@lucide/angular';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SedacajPreset } from './shared/theme/sedacaj-preset';
import { provideMarkdown } from 'ngx-markdown';
import { appRoutes } from './app.routes';
import { AxiosClient } from '@gishub/infrastructure';
import { Configuration } from './shared/configuration/configuration';
import { AppIconRegistry } from './shared/icons/app-icon-registry';
import { DarkModeProvider } from './shared/providers/dark-mode-provider';
import { HttpErrorHandlerService } from './shared/services/http-error-handler-service';
import { AuthProvider } from './features/auth/shared/auth-provider';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      const config = inject(Configuration);
      const errorHandler = inject(HttpErrorHandlerService);
      const auth = inject(AuthProvider);
      AxiosClient.setApiUrl(
        config,
        () => auth.state().accessToken,
        errorHandler,
        () => auth.notifier.refreshSession()
      );
    }),
    provideAppInitializer(() => {
      inject(DarkModeProvider);
    }),
    provideBrowserGlobalErrorListeners(),
    // Precarga por intención (hover/focus) en vez de PreloadAllModules: esta
    // última descarga los 28 chunks lazy de golpe al arrancar —incluido el
    // visor con @arcgis/core— y satura la red, haciendo lentos TODOS los
    // enlaces durante los primeros segundos.
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withPreloading(IntentPreloadingStrategy),
    ),
    provideHttpClient(),
    { provide: LUCIDE_ICONS, useValue: AppIconRegistry.ICONS },
    MessageService,
    ConfirmationService,
    provideMarkdown(),
    providePrimeNG({
      // Los overlays (selects, datepickers, multiselects…) se montan en el
      // body por defecto: los contenedores con overflow (ribbons de filtros,
      // drawers, tablas) recortaban los desplegables con el valor por defecto
      // del API ('self').
      overlayAppendTo: 'body',
      theme: {
        preset: SedacajPreset,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
      translation: {
        firstDayOfWeek: 1,
        dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
        dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
        dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
        monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
        monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
        dateFormat: 'dd/mm/yy',
        today: 'Hoy',
        clear: 'Limpiar',
        weekHeader: 'Sm',
        chooseDate: 'Elegir fecha',
        chooseMonth: 'Elegir mes',
        chooseYear: 'Elegir año',
        prevMonth: 'Mes anterior',
        nextMonth: 'Mes siguiente',
        prevYear: 'Año anterior',
        nextYear: 'Año siguiente',
        prevDecade: 'Década anterior',
        nextDecade: 'Década siguiente',
      },
    }),
  ],
};
